import { FunctionComponent, useContext } from 'react'

import dynamic from 'next/dynamic'
import styled, { css } from 'styled-components'

import { desktopCSS, mobileCSS, tabletCSS } from '@measures/responsive'
import contextArticleTextbox, {
  ArticleTextboxContextType,
} from '@contexts/articleTextbox'
import useHasInvertedColors from '@hooks/useHasInvertedColors'
import { TextWrapperProps } from '@widgets/ArticleTextbox/types'
import { bodyStyle } from '@widgets/Body/bodyStyle'

type StyledTextWrapperProps = Pick<TextWrapperProps, 'hideOnMobile'> &
  Pick<ArticleTextboxContextType, 'isPromo'> & {
    hasInvertedColors: boolean
  }

const HTMLView = dynamic(() => import('@components/HTMLView'))

const { ArticleTextboxContext } = contextArticleTextbox

const allowedHtmlTags = [
  'h2',
  'h3',
  'br',
  'p',
  'ul',
  'ol',
  'li',
  'em',
  'strong',
  'a',
  'sub',
  'sup',
]

const StyledTextWrapper = styled.div<StyledTextWrapperProps>`
  ${({
    theme: {
      colors: { textDefault, textBrand, staticWhite },
      spacing: { spacing8, spacing16, spacing20 },
    },
    hideOnMobile,
    isPromo,
    hasInvertedColors,
  }) => css`
    &&& {
      ${bodyStyle};
      color: ${hasInvertedColors ? staticWhite : textDefault};
      display: unset;

      ${isPromo &&
      css`
        margin-top: ${spacing20};
      `}
      &:empty {
        display: none;
      }

      p {
        margin: 0 0 ${spacing16} 0;
        overflow: initial;
      }

      p:last-child,
      ul:last-child {
        margin-bottom: 0;
      }

      ul {
        margin: ${spacing8} 0;
      }

      ol {
        margin: ${spacing8} 0;
        padding-left: 1.7em;
      }

      strong {
        font-weight: bold;
      }

      h1,
      h2,
      h3,
      h4,
      h5,
      h6 {
        margin: ${spacing16} 0 ${spacing8} 0;
      }

      em {
        font-style: italic;
      }

      a,
      a:active,
      a:visited,
      a:active {
        color: ${hasInvertedColors ? staticWhite : textDefault};
        font-weight: normal;
        text-decoration: underline;
      }

      a:hover {
        color: ${textBrand};
        text-decoration: underline;
      }

      ${desktopCSS(css`
        ${!hideOnMobile &&
        css`
          display: none;
        `}
      `)}

      ${tabletCSS(css`
        ${!hideOnMobile &&
        css`
          display: none;
        `}
      `)}

      ${mobileCSS(css`
        ${hideOnMobile &&
        css`
          display: none;
        `}
      `)}
    }
  `}
`

const TextWrapper: FunctionComponent<TextWrapperProps> = ({
  hideOnMobile,
  children,
}) => {
  const { isPromo } = useContext(ArticleTextboxContext)
  const hasInvertedColors = useHasInvertedColors()

  return (
    <StyledTextWrapper
      hideOnMobile={hideOnMobile}
      isPromo={isPromo}
      hasInvertedColors={hasInvertedColors}
    >
      <HTMLView
        content={children}
        allowedHtmlTags={allowedHtmlTags}
        inTextBox={true}
        brandedBox={isPromo}
      />
    </StyledTextWrapper>
  )
}

export default TextWrapper
